import { Box, Spinner, Text, VStack } from "@chakra-ui/react";
import React, { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArtifactProto, ChatClientType, chatClientTypeToJSON } from "build-ts/chat_session";
import { DarwinHeader } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import Message from "./Message";
import MessageInputBox from "./MessageInputBox";
import { useUser } from "context/UserContext";
import { ArtifactOpenOpts, ChatMessage } from "../../interfaces";
import { ArtifactKind, ArtifactMap } from "modules/artifacts/utils/artifactUtils";

export interface ChatPanelProps {
  isLoadingChat: boolean;
  chatId: string | undefined;
  chatMessages: ChatMessage[];
  sessionId?: string;
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  selectedAnalyst?: Header | null;
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isArtifactsPanelVisible?: boolean;
  scenarioJobs?: ScheduledJobProto[];
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  /** Full artifact registry for the current session — drives the @ mention menu. */
  artifactMap?: ArtifactMap;
  // ── Props provided by ChatContainer ──────────────────────────────────────
  isLoading?: boolean;
  streamingStatusLabel?: string;
  onSendMessage?: (
    inputMsg: string,
    newChat: boolean,
    analyst: Header | null | undefined,
    csvFiles?: { file: File; fileName: string }[]
  ) => Promise<void>;
  // True while an initialPrompt is waiting to auto-send
  // (e.g. while analysts are still loading after "Run Workflow").
  //
  isAutoSendPending?: boolean;
  currentSessionHeader?: DarwinHeader;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  isLoadingChat,
  chatId,
  chatMessages,
  sessionId,
  onArtifactClick,
  selectedAnalyst,
  isFromEva = false,
  analystsList = [],
  isLoadingAnalysts = false,
  onAnalystChange,
  isArtifactsPanelVisible = false,
  scenarioJobs,
  lookupByName,
  artifactMap,
  // Container-provided props
  isLoading = false,
  streamingStatusLabel = "Thinking ...",
  onSendMessage,
  isAutoSendPending = false,
  currentSessionHeader,
}) => {
  const navigate = useNavigate();

  const isInputDisabledDueToEva =
    isFromEva && !!currentSessionHeader && currentSessionHeader.type !== chatClientTypeToJSON(ChatClientType.EVA);

  // Block all send actions for new chats while the analysts list is still loading.
  // Without this guard the user would hit the "Please select an analyst first" error
  // even though an analyst is simply not yet resolved.
  const isBlockedByAnalystLoading = isLoadingAnalysts && !chatId && !sessionId;

  const { defaultTimeZone } = useUser();

  const onNewChat = useCallback(() => navigate("/chat/new"), [navigate]);

  // ── Scroll refs ───────────────────────────────────────────────────────────
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Becomes true when user manually scrolls away from the bottom.
  const userScrolledRef = useRef(false);
  // Tracks the previous message count so we can detect when a new message is added
  // vs. a deferred content-only reconciliation update to an existing message.
  const prevMessageCountRef = useRef(0);

  // Reset scroll refs synchronously in useLayoutEffect so they are zeroed before
  // the scroll check below runs in the same commit. useEffect fires after
  // useLayoutEffect, so placing ref mutations there would leave the scroll check
  // reading stale values on the first render of the new session.
  useLayoutEffect(() => {
    userScrolledRef.current = false;
    prevMessageCountRef.current = 0;
  }, [sessionId]);

  // ── Scroll helpers ────────────────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
      userScrolledRef.current = !isNearBottom;
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current && !userScrolledRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Auto-scroll only when:
  //   • Streaming is active (isLoading=true) — follow live token/step updates, OR
  //   • A new message was added (count increased) — scroll to show it.
  //
  // Deliberately excluded: deferred React.startTransition reconciliation updates
  // (e.g. applyStreamChunk finalising executionSteps / content after isLoading=false).
  // Those fire after the user has a chance to scroll away and would otherwise push
  // them back to the bottom on every deferred render. The count check ensures we
  // still scroll when the user sends a new message or a new AI response is appended.
  useLayoutEffect(() => {
    const newCount = chatMessages.length;
    const messageAdded = newCount > prevMessageCountRef.current;
    prevMessageCountRef.current = newCount;

    if (!isLoadingChat && newCount > 0 && (isLoading || messageAdded)) {
      scrollToBottom();
    }
  }, [chatMessages, isLoadingChat, isLoading, scrollToBottom]);

  // Wrap onSendMessage to reset scroll-to-bottom tracking on every send.
  const wrappedOnSend = useCallback(
    (
      msg: string,
      isNew: boolean,
      analyst: Header | null | undefined,
      csvFiles?: { file: File; fileName: string }[]
    ) => {
      userScrolledRef.current = false;
      return onSendMessage?.(msg, isNew, analyst, csvFiles);
    },
    [onSendMessage]
  );

  // hasMessages is passed to MessageInputBox so it can adapt placeholder and min-height.
  const hasMessages = useMemo(() => chatMessages.length > 0, [chatMessages.length]);

  // ── Loading state ─────────────────────────────────────────────────────────

  if (isLoadingChat) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
        <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
        <Text fontSize="md" color="gray.500" mb={0}>
          Loading...
        </Text>
      </Box>
    );
  }

  // This loader will be shown only when "Run Workflow" button is clicked.
  if (isAutoSendPending) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
        <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
        <Text fontSize="md" color="gray.500" mb={0}>
          Preparing your chat...
        </Text>
      </Box>
    );
  }

  // ── Messages layout ───────────────────────────────────────────────────────

  return (
    <VStack w="100%" h="100%" m={0} spacing={0} mt={1} position="relative">
      <Box
        ref={messagesContainerRef}
        flex="1"
        minH="0"
        w="100%"
        overflow="auto"
        onScroll={handleScroll}
        p={4}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--chakra-colors-gray-200)",
            borderRadius: "full",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "var(--chakra-colors-gray-400)",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "var(--chakra-colors-gray-400) transparent",
        }}
      >
        <VStack w={isArtifactsPanelVisible ? "100%" : "80%"} mx="auto" align="stretch" spacing={0}>
          <Box flex="1" minH="0" />
          {chatMessages?.map((msg, index) => (
            <Message
              key={msg.id ?? `${msg.author}-${index}`}
              author={msg.author as "user" | "ai"}
              content={msg.content}
              responseMessage={msg.responseMessage}
              artifacts={msg.artifacts}
              onArtifactClick={onArtifactClick}
              isLoading={isLoading && index === chatMessages.length - 1 && msg.author === "ai"}
              isLastMessage={index === chatMessages.length - 1}
              streamingStatusLabel={streamingStatusLabel}
              executionSteps={msg.executionSteps}
              streamingReasoningGroups={msg.streamingReasoningGroups}
              timelineOrder={msg.timelineOrder}
              prevMessageEndTimestampMs={msg.prevMessageEndTimestampMs}
              lsTraceUrl={isFromEva ? msg.ls_trace_url : undefined}
              // Perf optimization: only the latest AI message needs full chat history context
              // during active streaming; passing non-null chatMessages to older messages causes
              // avoidable re-renders on every token/chunk update.
              chatMessages={msg.author === "ai" && index === chatMessages.length - 1 ? chatMessages : undefined}
              isArtifactsPanelVisible={isArtifactsPanelVisible}
              lookupByName={lookupByName}
              scenarioJobs={scenarioJobs}
              userTimestampMs={msg.userTimestampMs}
              executionStatus={msg.executionStatus}
              defaultTimeZone={defaultTimeZone}
              onNewChat={onNewChat}
              fileArtifacts={msg.fileArtifacts}
            />
          ))}
        </VStack>
      </Box>
      <Box w="100%" p={4} bg="bg.surface" display="flex" flexDirection="column" alignItems="center" mb={1}>
        <MessageInputBox
          chatId={chatId}
          sessionId={sessionId}
          isLoading={isLoading}
          isLoadingChat={isLoadingChat}
          isInputDisabledDueToEva={isInputDisabledDueToEva}
          isBlockedByAnalystLoading={isBlockedByAnalystLoading}
          selectedAnalyst={selectedAnalyst}
          analystsList={analystsList}
          isLoadingAnalysts={isLoadingAnalysts}
          isFromEva={isFromEva}
          onAnalystChange={onAnalystChange}
          currentSessionHeader={currentSessionHeader}
          scenarioJobs={scenarioJobs}
          artifactMap={artifactMap}
          lookupByName={lookupByName}
          onSendMessage={wrappedOnSend}
          hasMessages={hasMessages}
          width={isArtifactsPanelVisible ? "95%" : "80%"}
        />
        <Text fontSize="xs" color="fg.muted" mt={2} mb={0}>
          Anko is AI and may make mistakes. Please double check responses.
        </Text>
      </Box>
    </VStack>
  );
};

export default ChatPanel;
